// utils/customerProvider.jsx
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAlert } from '../../hooks/useAlert';
import { UserContext } from './userContext';
import dayjs from 'dayjs';

const API_URL = process.env.REACT_APP_API_URL;

export function UserProvider({ children }) {
    const [users, setUsers] = useState([]);
    const [userDetail, setUserDetail] = useState(null);
    const [userStats, setUserStats] = useState([]);
    const [invoiceStats, setInvoiceStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statuses, setStatuses] = useState([]);
    const [listStatus, setListStatus] = useState([]);
    const [searchedUser, setSearchedUser] = useState([]);
    const [userCursor, setUserCursor] = useState(null);
    const [userHasMore, setUserHasMore] = useState(true);
    const userCursorRef = useRef(null);
    const userHasMoreRef = useRef(true);
    const searchTimeoutRef = useRef(null);
    const [usersTotal, setUsersTotal] = useState(0);

    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const fetchUsers = useCallback(async (reset = true, cursor = null, search = '') => {
        try {
            setLoading(true);

            if (reset) {
                setUserCursor(null);
                setUserHasMore(true);
                userCursorRef.current = null;
                userHasMoreRef.current = true;
            }

            const currentCursor = reset ? null : cursor ?? userCursorRef.current;
            if (!reset && !userHasMoreRef.current) {
                return;
            }

            const res = await axios.get(`${API_URL}/users`, {
                withCredentials: true,
                params: {
                    limit: 10,
                    cursor: currentCursor || undefined,
                    search: search || undefined,
                },
            });

            const fetched = res.data.users || [];
            const pagination = res.data.pagination || {};

            setUsers(prev => {
                if (reset) return fetched;
                const existingIds = new Set(prev.map((user) => user._id));
                const uniqueFetched = fetched.filter((user) => !existingIds.has(user._id));
                return [...prev, ...uniqueFetched];
            });
            
            setUsersTotal(pagination.totalCount);

            setUserCursor(pagination.nextCursor || null);
            userCursorRef.current = pagination.nextCursor || null;
            setUserHasMore(Boolean(pagination.hasMore));
            userHasMoreRef.current = Boolean(pagination.hasMore);
        } catch (err) {
            showAlert('Fehler beim Laden der User', 'error');
            console.log(err)
        } finally {
            setLoading(false);
        }
    }, [showAlert]);

    const fetchUserDetail = useCallback(async (id) => {
        try {
            const res = await axios.get(`${API_URL}/users/${id}`, {
                withCredentials: true
            });
            setUserDetail(res.data);
        } catch (err) {
            showAlert('Fehler beim Laden der User-Detailinformationen', 'error');
            console.log(err)
            return null;
        }
    }, [showAlert]);

    const saveUser = useCallback(async (userData) => {
        try {
            await axios.post(
                `${API_URL}/users`,
                userData,
                { withCredentials: true }
            );
            showAlert('User erfolgreich hinzugefügt', 'success');
            navigate('/admin/user');
        } catch (err) {
            showAlert('Fehler beim Speichern des Users', 'error');
            console.log(err)
        }
    }, [showAlert, navigate]);

    const searchUser = useCallback(async (searchTerm) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (!searchTerm) {
            setSearchedUser([]);
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const res = await axios.get(`${API_URL}/users`, {
                    withCredentials: true,
                    params: {
                        search: searchTerm,
                        limit: 50
                    }
                });

                setSearchedUser(res.data.users || []);
            } catch (err) {
                setSearchedUser([]);
                console.log(err)
            }
        }, 300);
    }, []);

    const editUser = useCallback(async (editData, id) => {
        try {
            const res = await axios.patch(
                `${API_URL}/users/${id}`,
                editData,
                { withCredentials: true }
            );
            setUserDetail(res.data);
            showAlert('User erfolgreich bearbeitet', 'success');
        } catch (error) {
            showAlert('Fehler beim Bearbeiten des Users', 'error');
            console.log(error)
        }
    }, [showAlert]);

    const fetchUserStats = useCallback(async () => {
        try {
            // User Stats
            const userRes = await axios.get(`${API_URL}/users`, { withCredentials: true });
            const resUsers = userRes.data.users || [];

            const groupedUsers = resUsers.reduce((acc, user) => {
                const monthKey = dayjs(user.signedUpAt).format('YYYY-MM');
                acc[monthKey] = (acc[monthKey] || 0) + 1;
                return acc;
            }, {});
        
            const chartUserData = Object.entries(groupedUsers)
                .map(([monthKey, count]) => ({
                    month: dayjs(monthKey).format('MMM YYYY'),
                    sortKey: monthKey,
                    user: count
                }))
                .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

            setUserStats(chartUserData);

            // Invoice Stats
            const invoiceRes = await axios.get(`${API_URL}/invoices/allInvoices?limit=100`, { withCredentials: true });
            const invoices = invoiceRes.data.data;

            const groupedInvoices = invoices.reduce((acc, invoice) => {
                const monthKey = dayjs(invoice.createdAt).format('YYYY-MM');
                acc[monthKey] = (acc[monthKey] || 0) + 1;
                return acc;
            }, {});

            const chartInvoiceData = Object.entries(groupedInvoices)
                .map(([monthKey, count]) => ({
                    month: dayjs(monthKey).format('MMM YYYY'),
                    sortKey: monthKey,
                    count: count
                }))
                .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

            setInvoiceStats(chartInvoiceData);
        } catch (error) {
            console.log(error);
        }
    }, [showAlert]);

    const value = {
        fetchUsers,
        users,
        fetchUserDetail,
        userDetail,
        fetchUserStats,
        userStats,
        invoiceStats,
        loading,
        saveUser,
        refetch: fetchUsers,
        statuses,
        listStatus,
        searchUser,
        searchedUser,
        editUser,
        userHasMore,
        userCursor,
        usersTotal
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
}